// src/utils/performanceMonitor.ts

import { errorTracking } from './errorTracking';

interface PerformanceMetrics {
  pageLoadTime?: number;
  firstContentfulPaint?: number;
  largestContentfulPaint?: number;
  firstInputDelay?: number;
  cumulativeLayoutShift?: number;
  renderTime?: number;
  memoryUsage?: {
    used: number;
    total: number;
    limit: number;
  };
}

class PerformanceMonitor {
  private traces: Map<string, any> = new Map();
  private metrics: PerformanceMetrics = {};
  private isInitialized = false;

  /**
   * パフォーマンス監視の初期化
   */
  init() {
    if (this.isInitialized) return;

    // Web Vitalsの監視
    this.observeWebVitals();

    // メモリ使用量の監視
    this.observeMemoryUsage();

    // レンダリングパフォーマンスの監視
    this.observeRenderingPerformance();

    this.isInitialized = true;
  }

  /**
   * Web Vitalsの監視
   */
  private observeWebVitals() {
    // First Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const fcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const fcpEntry = entries.find(entry => entry.name === 'first-contentful-paint');
          if (fcpEntry) {
            this.metrics.firstContentfulPaint = fcpEntry.startTime;
            this.logMetric('first_contentful_paint', fcpEntry.startTime);
          }
        });
        fcpObserver.observe({ entryTypes: ['paint'] });

        // Largest Contentful Paint
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.metrics.largestContentfulPaint = lastEntry.startTime;
          this.logMetric('largest_contentful_paint', lastEntry.startTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // First Input Delay
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            this.metrics.firstInputDelay = entry.processingStart - entry.startTime;
            this.logMetric('first_input_delay', this.metrics.firstInputDelay);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Cumulative Layout Shift
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0;
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.metrics.cumulativeLayoutShift = clsValue;
          this.logMetric('cumulative_layout_shift', clsValue);
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });

      } catch (error) {
        console.warn('PerformanceObserver not supported or error:', error);
      }
    }

    // Page Load Time (traditional)
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (perfData) {
          this.metrics.pageLoadTime = perfData.loadEventEnd - perfData.fetchStart;
          this.logMetric('page_load_time', this.metrics.pageLoadTime);
        }
      }, 0);
    });
  }

  /**
   * メモリ使用量の監視
   */
  private observeMemoryUsage() {
    if ('memory' in performance) {
      const checkMemory = () => {
        const memory = (performance as any).memory;
        this.metrics.memoryUsage = {
          used: memory.usedJSHeapSize,
          total: memory.totalJSHeapSize,
          limit: memory.jsHeapSizeLimit
        };

        // メモリ使用率が高い場合は警告ログ
        const usageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
        if (usageRatio > 0.8) {
          errorTracking.logCustomError('High memory usage detected', {
            memory_used: memory.usedJSHeapSize,
            memory_total: memory.totalJSHeapSize,
            memory_limit: memory.jsHeapSizeLimit,
            usage_ratio: usageRatio,
            is_ios: /iPad|iPhone|iPod/.test(navigator.userAgent)
          });
        }

        this.logMetric('memory_usage_mb', memory.usedJSHeapSize / 1024 / 1024);
      };

      // 初回チェック
      checkMemory();
      
      // 定期的にチェック（30秒間隔）
      setInterval(checkMemory, 30000);
    }
  }

  /**
   * レンダリングパフォーマンスの監視
   */
  private observeRenderingPerformance() {
    let frameCount = 0;
    let lastTime = performance.now();
    let fpsArray: number[] = [];

    const measureFPS = (currentTime: number) => {
      frameCount++;
      
      if (currentTime - lastTime >= 1000) {
        const fps = frameCount;
        fpsArray.push(fps);
        
        // 5秒間の平均FPSを計算
        if (fpsArray.length >= 5) {
          const avgFPS = fpsArray.reduce((a, b) => a + b, 0) / fpsArray.length;
          
          // FPSが低い場合は警告
          if (avgFPS < 30) {
            errorTracking.logCustomError('Low FPS detected', {
              average_fps: avgFPS,
              current_fps: fps,
              is_ios: /iPad|iPhone|iPod/.test(navigator.userAgent),
              memory_info: this.metrics.memoryUsage
            });
          }
          
          this.logMetric('average_fps', avgFPS);
          fpsArray = []; // リセット
        }
        
        frameCount = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(measureFPS);
    };

    requestAnimationFrame(measureFPS);
  }

  /**
   * カスタムトレースの開始
   */
  async startTrace(traceName: string) {
    const startTime = performance.now();
    const trace = await errorTracking.startTrace(traceName);
    
    // traceがnullの場合は、内部的なトレースのみ管理
    this.traces.set(traceName, {
      trace,
      startTime,
      name: traceName,
      isValid: trace !== null
    });

    return {
      stop: () => this.stopTrace(traceName)
    };
  }

  /**
   * カスタムトレースの停止
   */
  stopTrace(traceName: string) {
    const traceData = this.traces.get(traceName);
    if (!traceData) {
      console.warn(`Trace '${traceName}' not found or already stopped`);
      return 0;
    }

    const endTime = performance.now();
    const duration = endTime - traceData.startTime;

    // Firebase Performance Monitoring - 有効なtraceのみ停止
    if (traceData.trace && traceData.isValid) {
      try {
        traceData.trace.stop();
      } catch (error) {
        console.warn(`Failed to stop Firebase trace '${traceName}':`, error);
      }
    }

    // カスタムメトリクスとしてログ（traceが無効でも内部計測は記録）
    this.logMetric(`trace_${traceName}_duration`, duration);

    // iPhone特有の遅延を検出
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && duration > 5000) { // 5秒以上かかった場合
      errorTracking.logCustomError('iOS performance issue detected', {
        trace_name: traceName,
        duration_ms: duration,
        is_ios: true,
        memory_info: this.metrics.memoryUsage
      });
    }

    this.traces.delete(traceName);
    return duration;
  }

  /**
   * レンダリング時間の計測
   */
  measureRenderTime<T>(componentName: string, renderFn: () => T): T {
    const startTime = performance.now();
    const result = renderFn();
    const endTime = performance.now();
    const renderTime = endTime - startTime;

    this.metrics.renderTime = renderTime;
    
    // 長いレンダリング時間を検出
    if (renderTime > 100) { // 100ms以上
      errorTracking.logCustomError('Slow rendering detected', {
        component_name: componentName,
        render_time_ms: renderTime,
        is_ios: /iPad|iPhone|iPod/.test(navigator.userAgent),
        memory_info: this.metrics.memoryUsage
      });
    }

    this.logMetric(`render_time_${componentName}`, renderTime);
    return result;
  }

  /**
   * ネットワークリクエストの監視
   */
  monitorNetworkRequest(url: string, startTime: number, endTime: number, success: boolean) {
    const duration = endTime - startTime;
    
    this.logMetric('network_request_duration', duration);
    
    // 遅いリクエストや失敗したリクエストをログ
    if (duration > 10000 || !success) { // 10秒以上または失敗
      errorTracking.logCustomError('Network request issue', {
        url,
        duration_ms: duration,
        success,
        is_ios: /iPad|iPhone|iPod/.test(navigator.userAgent),
        connection_type: (navigator as any).connection?.effectiveType || 'unknown'
      });
    }
  }

  /**
   * メトリクスのログ出力
   */
  private logMetric(metricName: string, value: number) {
    const deviceInfo = {
      is_ios: /iPad|iPhone|iPod/.test(navigator.userAgent),
      is_safari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
      device_pixel_ratio: window.devicePixelRatio,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      window_width: window.innerWidth,
      window_height: window.innerHeight
    };

    // 数値とブール値を文字列に変換
    const stringDeviceInfo = {
      is_ios: deviceInfo.is_ios.toString(),
      is_safari: deviceInfo.is_safari.toString(),
      device_pixel_ratio: deviceInfo.device_pixel_ratio.toString(),
      screen_width: deviceInfo.screen_width.toString(),
      screen_height: deviceInfo.screen_height.toString(),
      window_width: deviceInfo.window_width.toString(),
      window_height: deviceInfo.window_height.toString()
    };
    
    errorTracking.logMetric(metricName, value, stringDeviceInfo);
  }

  /**
   * 現在のパフォーマンスメトリクスを取得
   */
  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * パフォーマンスレポートの生成
   */
  generateReport() {
    const report = {
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      metrics: this.getMetrics(),
      deviceInfo: {
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        connectionType: (navigator as any).connection?.effectiveType || 'unknown'
      }
    };

    // Performance reportは正常な情報なのでエラーとしてではなく、開発環境でのみログ出力
    if (import.meta.env.MODE === 'development') {}
    return report;
  }
}

export const performanceMonitor = new PerformanceMonitor();
